/**
 * B28 — §8.3 slot manifests: what an offline validator carries onto the
 * car deck. The manifest lists the slot's admittable tokens (ISSUED/ACTIVE
 * entitlements bound to the slot) and the revocation list (VOIDED ones).
 * EXHAUSTED and EXPIRED are deliberately in NEITHER list: they are not
 * admittable, and they are not revocations — an offline device denies a
 * token it cannot find (§8.3's manifest-lookup stance), which is exactly
 * the verdict a used or lapsed ticket deserves.
 *
 * Format mirrors §8.1 tokens — one verification vocabulary for validators:
 *
 *   tmf1.<base64url(canonical manifest JSON)>.<keyId>.<base64url(signature)>
 *
 * The signature covers `tmf1.<body>.<keyId>`; canonical JSON is fixed key
 * order with entries sorted by entitlement id, so building is deterministic:
 * the same store state and clock instant produce identical bytes.
 *
 * Tokens inside the manifest are minted from the SAME payload the delivery
 * email mints (ticketPayloadFor) — Ed25519 is deterministic, so the QR in
 * the buyer's inbox and the row in the validator's manifest are equal
 * strings, and offline lookup is a string match.
 *
 * PURITY: stores, signing, and clocks are injected ports. Object storage
 * (where the document lands, S3 in deployment) is the composition root's
 * business — the port lives here, the SDK does not.
 */
import type { Clock } from "./clock.js";
import { isTenantType, type TenantType } from "./vocabulary.js";
import type { CountingUnit, Entitlement, RedemptionModel } from "./entitlement.js";
import type { EntitlementStore } from "./redemption.js";
import {
  fromBase64Url,
  mintTicketToken,
  ticketPayloadFor,
  toBase64Url,
  type TicketSigner,
  type TicketVerifierPort,
} from "./ticket-token.js";

/* ---------------------------------------------------------------- types */

export interface ManifestEntry {
  readonly entitlementId: string;
  /** The §8.1 token, byte-equal to the delivered QR (see header). */
  readonly token: string;
  readonly model: RedemptionModel;
  readonly unit: CountingUnit;
  /** Balance snapshot at generation — advisory offline (§8.3: MULTI is online-required). */
  readonly remaining?: number;
  readonly validFrom?: number;
  readonly validUntil?: number;
}

export interface SlotManifest {
  readonly tenantId: string;
  readonly slotId: string;
  /**
   * B197 (RULED 2026-09-21, Option (2)) — the tenant's type ON the signed
   * document, so the offline scanner speaks the tenant's words with NO
   * network and no staleness (the device re-verifies this document before
   * custody; DeviceSession never carries the word). Optional: pre-B197
   * documents verify unchanged, and absence resolves to GENERIC downstream.
   */
  readonly tenantType?: TenantType;
  readonly generatedAt: number;
  readonly entries: readonly ManifestEntry[];
  readonly revokedEntitlementIds: readonly string[];
}

export class ManifestRefusedError extends Error {
  constructor(why: string) {
    super(`Refusing to build a manifest: ${why}`);
    this.name = "ManifestRefusedError";
  }
}

/** Storage/signing not configured on this stage — a named gap, not a half-answer. */
export class ManifestUnavailableError extends Error {
  constructor(why: string) {
    super(`Manifests are not available on this stage: ${why}`);
    this.name = "ManifestUnavailableError";
  }
}

/* ------------------------------------------------------- object storage */

export interface StoredManifestRef {
  readonly url: string;
  /** Epoch ms — when the presigned URL stops working. */
  readonly expiresAt: number;
}

/** Where manifest documents land; S3 + presigned GET in the composition root. */
export interface ManifestObjectStore {
  put(key: string, body: string, contentType: string): Promise<void>;
  presignGet(key: string, ttlMs: number): Promise<StoredManifestRef>;
}

/** Reference implementation: a Map and a fake URL scheme, for tests and stdio. */
export class InMemoryManifestObjectStore implements ManifestObjectStore {
  readonly objects = new Map<string, { body: string; contentType: string }>();

  constructor(private readonly clock: Clock) {}

  put(key: string, body: string, contentType: string): Promise<void> {
    this.objects.set(key, { body, contentType });
    return Promise.resolve();
  }

  presignGet(key: string, ttlMs: number): Promise<StoredManifestRef> {
    if (!this.objects.has(key)) return Promise.reject(new ManifestUnavailableError(`no object at ${key}`));
    const expiresAt = this.clock.now() + ttlMs;
    return Promise.resolve({ url: `https://manifests.invalid/${encodeURIComponent(key)}?expires=${expiresAt}`, expiresAt });
  }
}

/* ------------------------------------------------------------ canonical */

const VERSION = "tmf1";
const MANIFEST_KEYS = ["tenantId", "slotId", "tenantType", "generatedAt", "entries", "revokedEntitlementIds"] as const;
const ENTRY_KEYS = ["entitlementId", "token", "model", "unit", "remaining", "validFrom", "validUntil"] as const;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function canonicalEntry(entry: ManifestEntry): Record<string, unknown> {
  const ordered: Record<string, unknown> = {};
  for (const key of ENTRY_KEYS) {
    const value = entry[key];
    if (value !== undefined) ordered[key] = value;
  }
  return ordered;
}

/** Fixed key order, undefined omitted, entries/revocations sorted — deterministic bytes. */
function canonicalManifestJson(manifest: SlotManifest): string {
  const ordered: Record<string, unknown> = {};
  for (const key of MANIFEST_KEYS) {
    if (key === "entries") ordered[key] = manifest.entries.map(canonicalEntry);
    else if (key === "revokedEntitlementIds") ordered[key] = [...manifest.revokedEntitlementIds];
    // B197: undefined never lands in the canonical bytes — a document
    // without a tenantType stays byte-identical to the pre-B197 form.
    else if (manifest[key] !== undefined) ordered[key] = manifest[key];
  }
  return JSON.stringify(ordered);
}

/* -------------------------------------------------------------- builder */

export interface SlotManifestDeps {
  readonly entitlements: EntitlementStore;
  readonly signer: TicketSigner;
  readonly clock: Clock;
}

export interface BuiltManifest {
  /** The signed wire document — what lands in object storage. */
  readonly document: string;
  readonly manifest: SlotManifest;
}

const ADMITTABLE = new Set<Entitlement["state"]>(["ISSUED", "ACTIVE"]);

/** The ONE §8.2 selection — feeds the device manifest AND the office print (B74 R8). */
export interface ManifestPartition {
  readonly admittable: readonly Entitlement[];
  readonly revokedIds: readonly string[];
}

/**
 * Admittable = ISSUED/ACTIVE; VOIDED = revocation; EXHAUSTED/EXPIRED in
 * NEITHER list (absent ⇒ deny, §8.3). Both lists sorted by entitlement id
 * so every consumer is deterministic.
 */
export function partitionForManifest(bound: readonly Entitlement[]): ManifestPartition {
  const admittable: Entitlement[] = [];
  const revokedIds: string[] = [];
  for (const e of bound) {
    if (e.state === "VOIDED") revokedIds.push(e.entitlementId);
    else if (ADMITTABLE.has(e.state)) admittable.push(e);
  }
  admittable.sort((a, b) => a.entitlementId.localeCompare(b.entitlementId));
  revokedIds.sort((a, b) => a.localeCompare(b));
  return { admittable, revokedIds };
}

export async function buildSlotManifest(
  deps: SlotManifestDeps,
  input: { readonly tenantId: string; readonly slotId: string; readonly tenantType?: TenantType },
): Promise<BuiltManifest> {
  const { tenantId, slotId } = input;
  if (tenantId.trim().length === 0) throw new ManifestRefusedError("tenantId is required");
  if (slotId.trim().length === 0) throw new ManifestRefusedError("slotId is required");
  if (deps.signer.keyId.trim().length === 0 || deps.signer.keyId.includes(".")) {
    throw new ManifestRefusedError("signer keyId must be non-empty and dot-free");
  }

  const bound = await deps.entitlements.listBySlot(tenantId, slotId);
  const partition = partitionForManifest(bound); // the ONE §8.2 selection (shared with B74 R8's print)
  const entries: ManifestEntry[] = [];
  for (const e of partition.admittable) {
    const token = await mintTicketToken(ticketPayloadFor(e), deps.signer);
    entries.push({
      entitlementId: e.entitlementId,
      token,
      model: e.uses.model,
      unit: e.uses.unit,
      ...(e.uses.remaining !== undefined ? { remaining: e.uses.remaining } : {}),
      ...(e.validity.start !== undefined ? { validFrom: e.validity.start } : {}),
      validUntil: e.validity.end,
    });
  }
  const revoked = [...partition.revokedIds];

  const manifest: SlotManifest = Object.freeze({
    tenantId,
    slotId,
    // B197: the tenant's words ride the signed document (Option (2)).
    ...(input.tenantType !== undefined ? { tenantType: input.tenantType } : {}),
    generatedAt: deps.clock.now(),
    entries: Object.freeze(entries.map((e) => Object.freeze(e))),
    revokedEntitlementIds: Object.freeze(revoked),
  });
  const body = toBase64Url(encoder.encode(canonicalManifestJson(manifest)));
  const signedBytes = encoder.encode(`${VERSION}.${body}.${deps.signer.keyId}`);
  const signature = await deps.signer.sign(signedBytes);
  return { document: `${VERSION}.${body}.${deps.signer.keyId}.${toBase64Url(signature)}`, manifest };
}

/* ------------------------------------------------------------ verifier */

export type VerifiedManifest =
  | { readonly ok: true; readonly keyId: string; readonly manifest: SlotManifest }
  | { readonly ok: false; readonly why: "malformed" | "bad_signature" | "unknown_key" };

function parseEntry(raw: unknown): ManifestEntry | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(ENTRY_KEYS as readonly string[]).includes(key)) return undefined;
  }
  const { entitlementId, token, model, unit, remaining, validFrom, validUntil } = record;
  if (typeof entitlementId !== "string" || entitlementId.length === 0) return undefined;
  if (typeof token !== "string" || token.length === 0) return undefined;
  if (model !== "SINGLE" && model !== "MULTI" && model !== "UNLIMITED") return undefined;
  if (unit !== "PER_SCAN" && unit !== "PER_SERVICE_DAY") return undefined;
  for (const v of [remaining, validFrom, validUntil]) {
    if (v !== undefined && (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0)) return undefined;
  }
  return record as unknown as ManifestEntry;
}

function parseManifestPayload(json: string): SlotManifest | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(MANIFEST_KEYS as readonly string[]).includes(key)) return undefined;
  }
  const { tenantId, slotId, tenantType, generatedAt, entries, revokedEntitlementIds } = record;
  if (typeof tenantId !== "string" || tenantId.length === 0) return undefined;
  // B197: absent is fine (pre-B197 document); present must be a REAL
  // tenant type — a signed word list never smuggles junk vocabulary.
  if (tenantType !== undefined && (typeof tenantType !== "string" || !isTenantType(tenantType))) return undefined;
  if (typeof slotId !== "string" || slotId.length === 0) return undefined;
  if (typeof generatedAt !== "number" || !Number.isSafeInteger(generatedAt) || generatedAt < 0) return undefined;
  if (!Array.isArray(entries) || !Array.isArray(revokedEntitlementIds)) return undefined;
  const parsedEntries: ManifestEntry[] = [];
  for (const e of entries) {
    const parsed = parseEntry(e);
    if (parsed === undefined) return undefined;
    parsedEntries.push(parsed);
  }
  for (const id of revokedEntitlementIds) {
    if (typeof id !== "string" || id.length === 0) return undefined;
  }
  return {
    tenantId,
    slotId,
    ...(tenantType !== undefined ? { tenantType: tenantType as TenantType } : {}),
    generatedAt,
    entries: parsedEntries,
    revokedEntitlementIds: revokedEntitlementIds as string[],
  };
}

/** Fail-closed: garbage, extras, or a non-canonical body are all equally malformed. */
export async function verifySlotManifest(document: string, verifier: TicketVerifierPort): Promise<VerifiedManifest> {
  const malformed = { ok: false, why: "malformed" } as const;
  const parts = document.split(".");
  if (parts.length !== 4) return malformed;
  const [version, body, keyId, sig] = parts as [string, string, string, string];
  if (version !== VERSION || body.length === 0 || keyId.length === 0 || sig.length === 0) return malformed;
  const payloadBytes = fromBase64Url(body);
  const signature = fromBase64Url(sig);
  if (payloadBytes === undefined || signature === undefined || signature.length === 0) return malformed;
  const manifest = parseManifestPayload(decoder.decode(payloadBytes));
  if (manifest === undefined) return malformed;
  if (toBase64Url(encoder.encode(canonicalManifestJson(manifest))) !== body) return malformed;
  const result = await verifier.verify(encoder.encode(`${VERSION}.${body}.${keyId}`), signature, keyId);
  if (result !== "ok") return { ok: false, why: result };
  return { ok: true, keyId, manifest };
}
